import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminConfirmDialog,
  AdminField,
  AdminInput,
  AdminSelect,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminToastRegion,
  type AdminToast,
} from './AdminUi'
import '../styles/store-status-final-corrections.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
  idleExpiresAt: string
  absoluteExpiresAt: string
}

type StoreStatusSource = 'emergency-pause' | 'manual-override' | 'scheduled-exception' | 'staging-qa-override' | 'weekly-schedule'
type ExceptionMode = 'open' | 'closed'
type OverrideMode = 'none' | 'open' | 'closed'

type Schedule = {
  enabled: true
  timeZone: 'Pacific/Auckland'
  openWeekday: number
  openHour: number
  openMinute: number
  cutoffWeekday: number
  cutoffHour: number
  cutoffMinute: number
  closedWording: string
  closedSubtext: string
}

type ScheduledException = {
  id: string
  mode: ExceptionMode
  startsAt: string
  endsAt: string
  customerMessage: string | null
  enabled: boolean
  createdAt: string
  updatedAt: string
  updatedBy: string | null
}

type StoreStatus = {
  ordersOpen: boolean
  source: StoreStatusSource
  evaluatedAt: string
  scheduleOpen: boolean
  qaOverrideActive: boolean
  emergencyPaused: boolean
  customerMessage: string | null
  manualOverride: { mode: OverrideMode; until: string | null; active: boolean }
  scheduledException: { id: string | null; mode: 'none' | ExceptionMode; startsAt: string | null; endsAt: string | null; customerMessage: string | null; active: boolean }
  schedule: Schedule
  customClosedMessage: string | null
  updatedAt: string | null
  updatedBy: string | null
}

type StoreStatusPayload = {
  status: StoreStatus
  schedule: Schedule
  customClosedMessage: string | null
  exceptions: ScheduledException[]
  capacity: {
    limits: { saturday: number | null; sunday: number | null; weekend: number | null }
    usage: { saturday: number; sunday: number; weekend: number; windowStartsAt: string; windowEndsAt: string }
  }
}

type ConfirmAction = {
  title: string
  description: string
  confirmLabel: string
  dangerous: boolean
  run: () => Promise<void>
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const AUCKLAND_ZONE = 'Pacific/Auckland'

function pad(value: number) { return String(value).padStart(2, '0') }
function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}
function sourceLabel(source: StoreStatusSource) {
  if (source === 'emergency-pause') return 'Emergency pause'
  if (source === 'manual-override') return 'Temporary override'
  if (source === 'scheduled-exception') return 'Scheduled exception'
  if (source === 'staging-qa-override') return 'Staging QA override'
  return 'Weekly schedule'
}
function formatAucklandDateTime(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-NZ', { timeZone: AUCKLAND_ZONE, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date)
}
function zoneParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-NZ', { timeZone: AUCKLAND_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(date)
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return { year: Number(values.year), month: Number(values.month), day: Number(values.day), hour: Number(values.hour), minute: Number(values.minute), second: Number(values.second) }
}
function zoneOffsetMs(date: Date) {
  const parts = zoneParts(date)
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - date.getTime()
}
function aucklandInputToIso(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/)
  if (!match) return null
  const [, y, m, d, h, min] = match
  const localAsUtc = Date.UTC(Number(y), Number(m) - 1, Number(d), Number(h), Number(min), 0)
  let candidate = localAsUtc
  for (let index = 0; index < 3; index += 1) {
    const next = localAsUtc - zoneOffsetMs(new Date(candidate))
    if (Math.abs(next - candidate) < 1000) { candidate = next; break }
    candidate = next
  }
  return new Date(candidate).toISOString()
}
function isoToAucklandInput(value: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const parts = zoneParts(date)
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`
}
function scheduleDescription(schedule: Schedule) {
  return `${WEEKDAYS[schedule.openWeekday]} ${pad(schedule.openHour)}:${pad(schedule.openMinute)} → ${WEEKDAYS[schedule.cutoffWeekday]} ${pad(schedule.cutoffHour)}:${pad(schedule.cutoffMinute)}`
}
function timeValue(hour: number, minute: number) { return `${pad(hour)}:${pad(minute)}` }
function parseTime(value: string) {
  const match = value.match(/^(\d{2}):(\d{2})$/)
  if (!match) return null
  return { hour: Number(match[1]), minute: Number(match[2]) }
}
function capacityValue(value: number | null) { return value === null ? '' : String(value) }
function capacityLabel(value: number | null) { return value === null ? 'Unlimited' : String(value) }

export default function AdminStoreStatusPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [data, setData] = useState<StoreStatusPayload | null>(null)
  const [error, setError] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [toasts, setToasts] = useState<AdminToast[]>([])

  const [overrideMode, setOverrideMode] = useState<OverrideMode>('none')
  const [overrideExpiry, setOverrideExpiry] = useState<'until-cleared' | 'until-time'>('until-cleared')
  const [overrideUntil, setOverrideUntil] = useState('')
  const [closedMessage, setClosedMessage] = useState('')
  const [scheduleOpenDay, setScheduleOpenDay] = useState('1')
  const [scheduleOpenTime, setScheduleOpenTime] = useState('00:00')
  const [scheduleCutoffDay, setScheduleCutoffDay] = useState('5')
  const [scheduleCutoffTime, setScheduleCutoffTime] = useState('18:00')
  const [capacitySaturday, setCapacitySaturday] = useState('')
  const [capacitySunday, setCapacitySunday] = useState('')
  const [capacityWeekend, setCapacityWeekend] = useState('')

  const [editingExceptionId, setEditingExceptionId] = useState<string | null>(null)
  const [exceptionMode, setExceptionMode] = useState<ExceptionMode>('closed')
  const [exceptionStartsAt, setExceptionStartsAt] = useState('')
  const [exceptionEndsAt, setExceptionEndsAt] = useState('')
  const [exceptionMessage, setExceptionMessage] = useState('')

  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  function syncForms(payload: StoreStatusPayload) {
    setOverrideMode(payload.status.manualOverride.mode)
    setOverrideExpiry(payload.status.manualOverride.until ? 'until-time' : 'until-cleared')
    setOverrideUntil(isoToAucklandInput(payload.status.manualOverride.until))
    setClosedMessage(payload.customClosedMessage ?? '')
    setScheduleOpenDay(String(payload.schedule.openWeekday))
    setScheduleOpenTime(timeValue(payload.schedule.openHour, payload.schedule.openMinute))
    setScheduleCutoffDay(String(payload.schedule.cutoffWeekday))
    setScheduleCutoffTime(timeValue(payload.schedule.cutoffHour, payload.schedule.cutoffMinute))
    setCapacitySaturday(capacityValue(payload.capacity.limits.saturday))
    setCapacitySunday(capacityValue(payload.capacity.limits.sunday))
    setCapacityWeekend(capacityValue(payload.capacity.limits.weekend))
  }

  function resetExceptionForm() {
    setEditingExceptionId(null)
    setExceptionMode('closed')
    setExceptionStartsAt('')
    setExceptionEndsAt('')
    setExceptionMessage('')
  }

  async function loadStatus() {
    setError('')
    try {
      const [sessionResponse, statusResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin' }),
        fetch('/api/admin/store-status', { credentials: 'same-origin' }),
      ])
      if (sessionResponse.status === 401 || statusResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/store-status')
        return
      }
      if (!sessionResponse.ok || !statusResponse.ok) throw new Error('status unavailable')
      const nextSession = await sessionResponse.json() as SessionPayload
      const nextData = await statusResponse.json() as StoreStatusPayload
      setSession(nextSession)
      setData(nextData)
      syncForms(nextData)
    } catch {
      setError('Store Status could not be loaded. Please try again.')
    }
  }

  useEffect(() => { void loadStatus() }, [])

  function pushToast(title: string, message: string) {
    const id = crypto.randomUUID()
    setToasts([{ id, title, message, tone: 'success' }])
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, 4_000)
  }

  async function postChange(path: string, body: Record<string, unknown>, title: string, message: string) {
    if (!session || saving) return
    setSaving(true)
    setError('')
    try {
      const response = await fetch(path, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify(body),
      })
      const payload = await response.json().catch(() => null) as { message?: string } | null
      if (response.status === 401) { window.location.replace('/admin/login?next=/admin/store-status'); return }
      if (!response.ok) throw new Error(payload?.message || 'The change could not be saved.')
      await loadStatus()
      pushToast(title, message)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The change could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true); setError('')
    try {
      const response = await fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': session.csrfToken } })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch { setError('Logout could not be completed. Please try again.'); setLogoutBusy(false) }
  }

  function confirmEmergencyChange(paused: boolean) {
    setConfirmAction({
      title: paused ? 'Pause all new orders?' : 'Remove emergency pause?',
      description: paused ? 'New card checkout attempts and new bank-transfer orders will be blocked immediately. Existing card attempts can still finish safely.' : 'Emergency pause will stop taking priority and the next applicable Store Status control will decide.',
      confirmLabel: paused ? 'Pause orders now' : 'Remove emergency pause', dangerous: paused,
      run: () => postChange('/api/admin/store-status/emergency', { paused }, paused ? 'Orders paused' : 'Emergency pause removed', paused ? 'New orders are blocked by emergency pause.' : 'The next applicable control is active.'),
    })
  }

  function confirmOverride() {
    let until: string | null = null
    if (overrideMode !== 'none' && overrideExpiry === 'until-time') {
      until = aucklandInputToIso(overrideUntil)
      if (!until) { setError('Choose a valid Auckland end time for the temporary override.'); return }
    }
    const lifetime = overrideMode === 'none' ? '' : until ? `until ${formatAucklandDateTime(until)}` : 'until you clear it'
    setConfirmAction({
      title: overrideMode === 'none' ? 'Clear temporary override?' : `Force orders ${overrideMode}?`,
      description: overrideMode === 'none' ? 'The next applicable control will take over immediately.' : `Orders will be forced ${overrideMode} ${lifetime}. Emergency pause still has higher priority.`,
      confirmLabel: overrideMode === 'none' ? 'Clear override' : 'Apply temporary override', dangerous: overrideMode === 'closed',
      run: () => postChange(
        '/api/admin/store-status/override',
        { mode: overrideMode, until },
        overrideMode === 'none' ? 'Temporary override cleared' : 'Temporary override saved',
        overrideMode === 'none' ? 'The next applicable Store Status control is active.' : `Orders are forced ${overrideMode} ${lifetime}.`,
      ),
    })
  }

  function confirmClosedMessage() {
    setConfirmAction({
      title: closedMessage.trim() ? 'Save custom closed-store message?' : 'Use normal closed wording?',
      description: closedMessage.trim() ? 'Customers will see this wording whenever the store is closed unless an active closed scheduled exception has its own message.' : 'The custom message will be cleared and the normal approved closed wording will be used.',
      confirmLabel: 'Save closed message', dangerous: false,
      run: () => postChange('/api/admin/store-status/message', { message: closedMessage.trim() || null }, 'Closed message saved', closedMessage.trim() ? 'Custom closed-store wording is active.' : 'Normal closed wording is the fallback.'),
    })
  }

  function confirmSchedule() {
    const openTime = parseTime(scheduleOpenTime); const cutoffTime = parseTime(scheduleCutoffTime)
    if (!openTime || !cutoffTime) { setError('Choose valid weekly schedule times.'); return }
    const body = { openWeekday: Number(scheduleOpenDay), openHour: openTime.hour, openMinute: openTime.minute, cutoffWeekday: Number(scheduleCutoffDay), cutoffHour: cutoffTime.hour, cutoffMinute: cutoffTime.minute }
    setConfirmAction({
      title: 'Change normal weekly schedule?',
      description: `The baseline order window will become ${WEEKDAYS[body.openWeekday]} ${scheduleOpenTime} → ${WEEKDAYS[body.cutoffWeekday]} ${scheduleCutoffTime}, Pacific/Auckland. Higher-priority controls remain unchanged.`,
      confirmLabel: 'Save weekly schedule', dangerous: false,
      run: () => postChange('/api/admin/store-status/schedule', body, 'Weekly schedule saved', 'The normal Pacific/Auckland order window has been updated.'),
    })
  }

  function parseLimit(value: string) {
    if (!value.trim()) return null
    const parsed = Number(value)
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined
  }

  function confirmCapacity() {
    const saturday = parseLimit(capacitySaturday); const sunday = parseLimit(capacitySunday); const weekend = parseLimit(capacityWeekend)
    if (saturday === undefined || sunday === undefined || weekend === undefined) { setError('Capacity must be a whole number of zero or more, or blank for unlimited.'); return }
    setConfirmAction({
      title: 'Save order capacity limits?',
      description: `Saturday: ${capacityLabel(saturday)} · Sunday: ${capacityLabel(sunday)} · Weekend total: ${capacityLabel(weekend)}. New checkout attempts will be enforced against these limits.`,
      confirmLabel: 'Save capacity', dangerous: false,
      run: () => postChange('/api/admin/store-status/capacity', { saturday, sunday, weekend }, 'Capacity saved', 'Checkout capacity enforcement is using the saved limits.'),
    })
  }

  function startEditException(item: ScheduledException) {
    setEditingExceptionId(item.id)
    setExceptionMode(item.mode)
    setExceptionStartsAt(isoToAucklandInput(item.startsAt))
    setExceptionEndsAt(isoToAucklandInput(item.endsAt))
    setExceptionMessage(item.customerMessage ?? '')
  }

  function confirmExceptionSave() {
    const startsAt = aucklandInputToIso(exceptionStartsAt); const endsAt = aucklandInputToIso(exceptionEndsAt)
    if (!startsAt || !endsAt) { setError('Choose both the start and end time for the scheduled exception.'); return }
    const editing = Boolean(editingExceptionId)
    setConfirmAction({
      title: editing ? 'Update scheduled exception?' : 'Add scheduled exception?',
      description: `Orders will be forced ${exceptionMode} from ${formatAucklandDateTime(startsAt)} until ${formatAucklandDateTime(endsAt)} unless a higher-priority control takes over.`,
      confirmLabel: editing ? 'Update exception' : 'Add exception', dangerous: exceptionMode === 'closed',
      run: async () => {
        await postChange(editingExceptionId ? `/api/admin/store-status/exceptions/${editingExceptionId}` : '/api/admin/store-status/exceptions', { mode: exceptionMode, startsAt, endsAt, customerMessage: exceptionMessage.trim() || null }, editing ? 'Exception updated' : 'Exception added', 'Scheduled exceptions have been updated.')
        resetExceptionForm()
      },
    })
  }

  function confirmExceptionEnabled(item: ScheduledException) {
    setConfirmAction({
      title: item.enabled ? 'Disable scheduled exception?' : 'Enable scheduled exception?',
      description: item.enabled ? 'The exception will remain saved but will no longer affect Store Status.' : 'The exception will become eligible to affect Store Status during its saved time window.',
      confirmLabel: item.enabled ? 'Disable exception' : 'Enable exception', dangerous: false,
      run: () => postChange(`/api/admin/store-status/exceptions/${item.id}/enabled`, { enabled: !item.enabled }, item.enabled ? 'Exception disabled' : 'Exception enabled', 'Scheduled exception state updated.'),
    })
  }

  function confirmExceptionRemove(item: ScheduledException) {
    setConfirmAction({
      title: 'Remove scheduled exception?', description: 'This removes the saved exception record. Use Disable instead if you may want to reuse it.', confirmLabel: 'Remove exception', dangerous: true,
      run: async () => { await postChange(`/api/admin/store-status/exceptions/${item.id}/remove`, {}, 'Exception removed', 'The scheduled exception was removed.'); if (editingExceptionId === item.id) resetExceptionForm() },
    })
  }

  async function executeConfirmedAction() {
    const action = confirmAction
    if (!action || saving) return
    setConfirmAction(null)
    await action.run()
  }

  if (error && (!session || !data)) {
    return <main className="admin-shell-gate-error"><AdminStatePanel state="error" title="Store Status unavailable" description={error} action={<AdminButton type="button" onClick={() => void loadStatus()}>Retry</AdminButton>} /></main>
  }
  if (!session || !data) {
    return <main className="admin-shell-gate-loading" aria-label="Loading Store Status"><AdminCard><AdminSkeleton lines={6} /></AdminCard></main>
  }

  const { status, schedule, exceptions, capacity } = data
  const canReviewOverride = overrideMode !== 'none' || status.manualOverride.mode !== 'none'

  return (
    <AdminShell
      title="Store Status" eyebrow="OVERVIEW"
      description="Control order availability, exceptions, weekly hours and safe checkout capacity from one authoritative operational layer."
      user={session.user} onLogout={() => void logout()} logoutBusy={logoutBusy} environmentLabel={environmentLabel}
      actions={<AdminButton variant="secondary" type="button" disabled={saving} onClick={() => void loadStatus()}>Refresh status</AdminButton>}
    >
      {error && <div className="admin-store-status__inline-error" role="alert">{error}</div>}

      <AdminCard className={`admin-store-status__hero ${status.ordersOpen ? 'is-open' : 'is-closed'}`}>
        <div className="admin-store-status__hero-main">
          <div><span className="admin-store-status__eyebrow">EFFECTIVE RIGHT NOW</span><h2>{status.ordersOpen ? 'Orders are open' : 'Orders are closed'}</h2><p>{status.ordersOpen ? 'Customers can start new orders right now.' : status.customerMessage ?? 'New orders are blocked right now.'}</p></div>
          <AdminStatusPill tone={status.ordersOpen ? 'success' : 'danger'}>{status.ordersOpen ? 'OPEN' : 'CLOSED'}</AdminStatusPill>
        </div>
        <dl className="admin-store-status__hero-details">
          <div><dt>Why</dt><dd>{sourceLabel(status.source)}</dd></div>
          <div><dt>Weekly schedule</dt><dd>{status.scheduleOpen ? 'Open now' : 'Closed now'}</dd></div>
          <div><dt>Last status change</dt><dd>{status.updatedBy ? `${formatAucklandDateTime(status.updatedAt)} · ${status.updatedBy}` : 'No manual changes yet'}</dd></div>
        </dl>
        {status.qaOverrideActive && <p className="admin-store-status__qa-note">Staging QA override is active. Real business controls above it still take priority.</p>}
      </AdminCard>

      <section className="admin-store-status__control-grid" aria-label="Store status controls">
        <AdminCard className="admin-store-status__control-card admin-store-status__control-card--danger">
          <AdminCardHeader title="Emergency pause" description="Highest priority. Stop new orders immediately." action={<AdminStatusPill tone={status.emergencyPaused ? 'danger' : 'neutral'}>{status.emergencyPaused ? 'Active' : 'Off'}</AdminStatusPill>} />
          <p className="admin-store-status__control-copy">{status.emergencyPaused ? 'All new checkout attempts are blocked. Existing card attempts can still complete safely.' : 'Emergency pause is off.'}</p>
          <AdminButton variant={status.emergencyPaused ? 'secondary' : 'danger'} type="button" disabled={saving} onClick={() => confirmEmergencyChange(!status.emergencyPaused)}>{status.emergencyPaused ? 'Remove emergency pause' : 'Pause orders now'}</AdminButton>
        </AdminCard>

        <AdminCard className="admin-store-status__control-card">
          <AdminCardHeader title="Temporary override" description="Force orders open or closed until cleared, or until a chosen Auckland time." action={status.manualOverride.active ? <AdminStatusPill tone="pink">Active</AdminStatusPill> : undefined} />
          <div className="admin-store-status__form-grid admin-store-status__form-grid--three">
            <AdminField label="Temporary state"><AdminSelect value={overrideMode} onChange={(event) => setOverrideMode(event.target.value as OverrideMode)}><option value="none">No override</option><option value="open">Force open</option><option value="closed">Force closed</option></AdminSelect></AdminField>
            <AdminField label="Expiry"><AdminSelect value={overrideExpiry} disabled={overrideMode === 'none'} onChange={(event) => setOverrideExpiry(event.target.value as typeof overrideExpiry)}><option value="until-cleared">Until cleared</option><option value="until-time">At a specified time</option></AdminSelect></AdminField>
            <AdminField label="Ends at" hint="Pacific/Auckland local time"><AdminInput type="datetime-local" value={overrideUntil} disabled={overrideMode === 'none' || overrideExpiry === 'until-cleared'} onChange={(event) => setOverrideUntil(event.target.value)} /></AdminField>
          </div>
          {status.manualOverride.mode !== 'none' && <p className="admin-store-status__current-rule">Saved override: <strong>{status.manualOverride.mode === 'open' ? 'Open' : 'Closed'}</strong> {status.manualOverride.until ? `until ${formatAucklandDateTime(status.manualOverride.until)}` : 'until cleared'}{!status.manualOverride.active && ' · not currently active'}</p>}
          {canReviewOverride ? <AdminButton type="button" disabled={saving} onClick={confirmOverride}>{overrideMode === 'none' ? 'Clear override' : 'Review override'}</AdminButton> : null}
        </AdminCard>

        <AdminCard className="admin-store-status__control-card admin-store-status__control-card--wide">
          <AdminCardHeader title="Closed-store message" description="Optional customer-facing wording used when ordering is closed. Blank uses the normal approved closed wording." />
          <AdminField label="Custom closed message" hint={`${closedMessage.length}/240 characters`}>
            <textarea className="admin-ui-input admin-store-status__textarea" maxLength={240} value={closedMessage} onChange={(event) => setClosedMessage(event.target.value)} placeholder={schedule.closedWording} />
          </AdminField>
          <AdminButton type="button" disabled={saving} onClick={confirmClosedMessage}>Review message</AdminButton>
        </AdminCard>
      </section>

      <AdminCard className="admin-store-status__wide-card">
        <AdminCardHeader title="Scheduled exceptions" description="Add multiple future closures or special openings. Enabled exceptions may not overlap; disable or edit an existing one first." action={status.scheduledException.active ? <AdminStatusPill tone="info">One active now</AdminStatusPill> : undefined} />
        <div className="admin-store-status__form-grid admin-store-status__form-grid--four">
          <AdminField label="Exception state"><AdminSelect value={exceptionMode} onChange={(event) => setExceptionMode(event.target.value as ExceptionMode)}><option value="closed">Force closed</option><option value="open">Force open</option></AdminSelect></AdminField>
          <AdminField label="Starts at" hint="Pacific/Auckland"><AdminInput type="datetime-local" value={exceptionStartsAt} onChange={(event) => setExceptionStartsAt(event.target.value)} /></AdminField>
          <AdminField label="Ends at" hint="Pacific/Auckland"><AdminInput type="datetime-local" value={exceptionEndsAt} onChange={(event) => setExceptionEndsAt(event.target.value)} /></AdminField>
          <AdminField label="Customer message" hint="Optional; used for a closed exception"><AdminInput maxLength={240} value={exceptionMessage} onChange={(event) => setExceptionMessage(event.target.value)} /></AdminField>
        </div>
        <div className="admin-store-status__button-row"><AdminButton type="button" disabled={saving} onClick={confirmExceptionSave}>{editingExceptionId ? 'Review changes' : 'Add exception'}</AdminButton>{editingExceptionId && <AdminButton type="button" variant="ghost" onClick={resetExceptionForm}>Cancel edit</AdminButton>}</div>
        <div className="admin-store-status__exception-list">
          {exceptions.length === 0 ? <p className="admin-store-status__empty-copy">No scheduled exceptions saved.</p> : exceptions.map((item) => (
            <article key={item.id} className={`admin-store-status__exception ${item.enabled ? '' : 'is-disabled'}`}>
              <div><div className="admin-store-status__exception-heading"><strong>{item.mode === 'closed' ? 'Closed' : 'Open'}</strong><AdminStatusPill tone={item.enabled ? 'info' : 'neutral'}>{item.enabled ? 'Enabled' : 'Disabled'}</AdminStatusPill>{status.scheduledException.id === item.id && <AdminStatusPill tone="pink">Active now</AdminStatusPill>}</div><p>{formatAucklandDateTime(item.startsAt)} → {formatAucklandDateTime(item.endsAt)}</p>{item.customerMessage && <small>Customer message: {item.customerMessage}</small>}</div>
              <div className="admin-store-status__exception-actions"><AdminButton compact variant="secondary" type="button" onClick={() => startEditException(item)}>Edit</AdminButton><AdminButton compact variant="ghost" type="button" onClick={() => confirmExceptionEnabled(item)}>{item.enabled ? 'Disable' : 'Enable'}</AdminButton><AdminButton compact variant="danger" type="button" onClick={() => confirmExceptionRemove(item)}>Remove</AdminButton></div>
            </article>
          ))}
        </div>
      </AdminCard>

      <section className="admin-store-status__control-grid" aria-label="Operational configuration">
        <AdminCard className="admin-store-status__control-card">
          <AdminCardHeader title="Normal weekly schedule" description="Authoritative baseline in Pacific/Auckland when no higher-priority control is active." action={<AdminStatusPill tone={status.scheduleOpen ? 'success' : 'neutral'}>{status.scheduleOpen ? 'Open now' : 'Closed now'}</AdminStatusPill>} />
          <div className="admin-store-status__form-grid admin-store-status__form-grid--four">
            <AdminField label="Opens day"><AdminSelect value={scheduleOpenDay} onChange={(event) => setScheduleOpenDay(event.target.value)}>{WEEKDAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}</AdminSelect></AdminField>
            <AdminField label="Opens time"><AdminInput type="time" value={scheduleOpenTime} onChange={(event) => setScheduleOpenTime(event.target.value)} /></AdminField>
            <AdminField label="Cutoff day"><AdminSelect value={scheduleCutoffDay} onChange={(event) => setScheduleCutoffDay(event.target.value)}>{WEEKDAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}</AdminSelect></AdminField>
            <AdminField label="Cutoff time"><AdminInput type="time" value={scheduleCutoffTime} onChange={(event) => setScheduleCutoffTime(event.target.value)} /></AdminField>
          </div>
          <p className="admin-store-status__current-rule">Saved baseline: <strong>{scheduleDescription(schedule)}</strong> · {schedule.timeZone}</p>
          <AdminButton type="button" disabled={saving} onClick={confirmSchedule}>Review weekly schedule</AdminButton>
        </AdminCard>

        <AdminCard className="admin-store-status__control-card">
          <AdminCardHeader title="Order capacity" description="Authoritative order limits for the current drop. Blank means unlimited." />
          <div className="admin-store-status__form-grid admin-store-status__form-grid--three">
            <AdminField label="Saturday limit" hint={`Used: ${capacity.usage.saturday}`}><AdminInput inputMode="numeric" min="0" type="number" value={capacitySaturday} placeholder="Unlimited" onChange={(event) => setCapacitySaturday(event.target.value)} /></AdminField>
            <AdminField label="Sunday limit" hint={`Used: ${capacity.usage.sunday}`}><AdminInput inputMode="numeric" min="0" type="number" value={capacitySunday} placeholder="Unlimited" onChange={(event) => setCapacitySunday(event.target.value)} /></AdminField>
            <AdminField label="Weekend total limit" hint={`Used: ${capacity.usage.weekend}`}><AdminInput inputMode="numeric" min="0" type="number" value={capacityWeekend} placeholder="Unlimited" onChange={(event) => setCapacityWeekend(event.target.value)} /></AdminField>
          </div>
          <p className="admin-store-status__current-rule">Capacity window: {formatAucklandDateTime(capacity.usage.windowStartsAt)} → {formatAucklandDateTime(capacity.usage.windowEndsAt)}</p>
          <AdminButton type="button" disabled={saving} onClick={confirmCapacity}>Review capacity</AdminButton>
        </AdminCard>
      </section>

      <AdminCard className="admin-store-status__priority-card">
        <AdminCardHeader title="Which control wins?" description="Boss Zone always evaluates Store Status in this order." />
        <ol className="admin-store-status__priority-list">
          <li><strong>1</strong><span><b>Emergency pause</b><small>Immediate stop. Highest priority.</small></span></li>
          <li><strong>2</strong><span><b>Temporary override</b><small>Explicit open/closed state until cleared or its optional expiry.</small></span></li>
          <li><strong>3</strong><span><b>Scheduled exception</b><small>Enabled planned open/closed period.</small></span></li>
          <li><strong>4</strong><span><b>Weekly schedule</b><small>{scheduleDescription(schedule)}, {schedule.timeZone}.</small></span></li>
        </ol>
      </AdminCard>

      <AdminConfirmDialog open={Boolean(confirmAction)} title={confirmAction?.title ?? ''} description={confirmAction?.description ?? ''} confirmLabel={confirmAction?.confirmLabel ?? 'Confirm'} dangerous={confirmAction?.dangerous} onConfirm={() => void executeConfirmedAction()} onCancel={() => setConfirmAction(null)} />
      <AdminToastRegion toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
    </AdminShell>
  )
}
